import React, { useRef, useState } from "react";
import { TextPromptProposalChips } from "@/components/flow/build/Steps/Prompt/SidebarContent/Annotate/TextPromptProposalChips";
import Button from "@scripts/react/components/Button";
import { PromptingTipsCard } from "@/components/tutorial/PromptingTipsCard";
import { LabelingGuide } from "@/components/tutorial/LabelingGuide";

interface NoResultsEmptyStateProps {
    labelName: string;
    /** Whether this label uses box prompting (has diffs + annotations to use as prompts) */
    isBoxPrompting?: boolean;
    /** Callback for "Expand Search" button when using box prompting */
    onExpandSearch?: () => void;
    /** Whether the expand search operation is in progress */
    isExpandingSearch?: boolean;
}

export const NoResultsEmptyState: React.FC<NoResultsEmptyStateProps> = ({
    labelName,
    isBoxPrompting = false,
    onExpandSearch,
    isExpandingSearch = false
}) => {
    const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);
    const [isLabelingGuideOpen, setIsLabelingGuideOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleOpenLabelingGuide = () => {
        setIsTipsModalOpen(false);
        setIsLabelingGuideOpen(true);
    };

    // For labels using box prompting, show a simpler UI with "Expand search" button
    if (isBoxPrompting) {
        return (
            <div className="flex flex-col border-t border-gray-300">
                <span className="flex flex-col gap-1 p-4 text-xs font-normal text-tertiary">
                    <p>No matches yet. We can widen the search to find more.</p>
                </span>
                <div className="flex w-full justify-end px-1 pb-1">
                    {/* @ts-expect-error - Button component type issue */}
                    <Button
                        type="purbo-ghost"
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onExpandSearch?.();
                        }}
                        disabled={isExpandingSearch}
                    >
                        {isExpandingSearch ? (
                            <>
                                <i className="far fa-spinner-third fa-spin" />
                                Searching...
                            </>
                        ) : (
                            <>
                                <i className="far fa-search-plus" />
                                Expand Search
                            </>
                        )}
                    </Button>
                </div>
            </div>
        );
    }

    // Default UI for labels without user edits - show text prompt suggestions
    return (
        <div className="flex flex-col gap-2 border-t border-gray-300 p-4">
            <span className="flex flex-col gap-1 text-xs font-normal text-tertiary">
                <p>No objects found.</p>
                <p>Draw a box around it or try another word: </p>
            </span>
            {/* Hide the "Try these:" header from TextPromptProposalChips using CSS */}
            <div className="[&>div>div:first-child]:hidden">
                <TextPromptProposalChips
                    isDisabled={false}
                    showPoweredBy={false}
                    sourceLabel={labelName}
                />
            </div>
            <div className="flex w-full pt-2">
                {/* @ts-expect-error - Button component type issue */}
                <Button
                    forwardedRef={buttonRef}
                    className="w-full !bg-gray-100"
                    type="ghost"
                    size="xsmall"
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setIsTipsModalOpen((prev) => !prev);
                    }}
                >
                    <i className="far fa-info-circle" />
                    Prompting Tips
                </Button>
            </div>
            <PromptingTipsCard
                isOpen={isTipsModalOpen}
                onClose={() => setIsTipsModalOpen(false)}
                anchorElement={buttonRef.current}
                onOpenLabelingGuide={handleOpenLabelingGuide}
            />
            <LabelingGuide
                isOpen={isLabelingGuideOpen}
                onClose={() => setIsLabelingGuideOpen(false)}
            />
        </div>
    );
};
